<template>
  <div v-if="hasReadPermission" class="agent-stage-prompts">
    <div class="section-header">
      <h3 class="section-title ds-panel-title">
        Stages
      </h3>
      <v-btn
        v-if="hasWritePermission"
        text
        elevation="0"
        class="mank-btn add-stage-btn"
        :disabled="showAddStageForm"
        @click="toggleAddStageForm">
        <Icon name="plus" variant="solid" size="16" class="mr-1"/>
        Add stage
      </v-btn>
    </div>

    <!-- Add stage form -->
    <div v-if="showAddStageForm" class="stage-card add-stage-form">
      <div class="form-group">
        <label class="form-label required">
          Name
          <span class="required-asterisk">*</span>
        </label>
        <MankTextField
          v-model="newStage.name"
          placeholder="e.g. greeting"
          hide-details="auto"/>
      </div>
      <div class="form-group">
        <label class="form-label required">
          Initial prompt
          <span class="required-asterisk">*</span>
        </label>
        <MankTextarea
          v-model="newStage.initial_prompt_content"
          placeholder="You are a friendly greeter..."
          rows="3"
          hide-details="auto"/>
      </div>
      <div class="form-group">
        <Checkbox
          v-model="newStage.is_initial"
          label="Initial stage (the call starts here)"/>
      </div>
      <div class="form-actions">
        <v-btn text elevation="0" class="mank-btn" @click="toggleAddStageForm">
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          elevation="0"
          class="mank-btn"
          :loading="addStageLoading"
          :disabled="!newStage.name.trim() || !newStage.initial_prompt_content.trim()"
          @click="submitNewStage">
          Create stage
        </v-btn>
      </div>
    </div>

    <div v-if="loadingStages" class="stages-loading">
      Loading stages...
    </div>

    <div v-else-if="!stages.length && !showAddStageForm" class="stages-empty">
      <p>This agent has no stages yet.</p>
      <v-btn
        v-if="hasWritePermission"
        text
        elevation="0"
        class="mank-btn"
        @click="toggleAddStageForm">
        Create the first stage
      </v-btn>
    </div>

    <div v-else class="stages-list">
      <div v-for="stage in stages" :key="stage.id" class="stage-accordion">
        <div class="stage-header" @click="toggleStage(stage)">
          <div class="stage-header-left">
            <h4 class="stage-title">
              {{ stage.name }}
            </h4>
            <span class="chip chip-muted">Sıra {{ stage.position }}</span>
            <span v-if="stage.is_initial" class="chip chip-primary">Initial</span>
          </div>
          <div class="stage-header-actions">
            <v-btn
              v-if="hasWritePermission"
              icon
              size="small"
              variant="text"
              class="mank-btn-icon"
              @click.stop="startEditStage(stage)">
              <Icon name="edit" variant="solid" size="16"/>
            </v-btn>
            <v-btn
              v-if="hasWritePermission"
              icon
              size="small"
              variant="text"
              class="mank-btn-icon"
              :disabled="stages.length <= 1"
              :title="stages.length <= 1 ? 'An agent must have at least one stage' : 'Delete stage'"
              @click.stop="askDeleteStage(stage)">
              <Icon name="trash" variant="solid" size="16"/>
            </v-btn>
            <v-btn
              icon
              size="small"
              variant="text"
              class="expand-btn"
              :class="{ expanded: stage.expanded }">
              <v-icon size="18" color="on-surface">
                mdi-chevron-up
              </v-icon>
            </v-btn>
          </div>
        </div>

        <div v-if="stage.expanded" class="stage-content">
          <!-- Edit stage metadata -->
          <div v-if="stage.editingMeta" class="stage-edit-form">
            <div class="form-group">
              <label class="form-label required">
                Name
                <span class="required-asterisk">*</span>
              </label>
              <MankTextField v-model="stage.editForm.name" hide-details="auto"/>
            </div>
            <div class="form-group">
              <label class="form-label">Position</label>
              <MankTextField v-model="stage.editForm.position" type="number" hide-details="auto"/>
            </div>
            <div class="form-group">
              <Checkbox
                v-model="stage.editForm.is_initial"
                label="Initial stage (the call starts here)"/>
            </div>
            <div class="form-actions">
              <v-btn text elevation="0" class="mank-btn" @click="cancelEditStage(stage)">
                Cancel
              </v-btn>
              <v-btn
                color="primary"
                elevation="0"
                class="mank-btn"
                :loading="stage.savingMeta"
                :disabled="!stage.editForm.name.trim()"
                @click="submitEditStage(stage)">
                Save
              </v-btn>
            </div>
          </div>

          <!-- Prompt version history -->
          <div class="prompt-versions">
            <div
              v-for="prompt in stage.prompts"
              :key="prompt.id"
              class="prompt-version-row"
              :class="{ 'is-active': prompt.is_active }">
              <div class="prompt-version-header">
                <span class="chip chip-muted">v{{ prompt.version }}</span>
                <span v-if="prompt.is_active" class="chip chip-success">Yayında</span>
                <span v-if="usageFor(stage, prompt) !== null" class="usage-text">
                  {{ usageFor(stage, prompt) }} çağrıda kullanıldı
                </span>
                <div class="prompt-version-spacer"/>
                <v-btn
                  v-if="hasWritePermission && !prompt.is_active"
                  text
                  x-small
                  elevation="0"
                  class="mank-btn activate-btn"
                  :loading="stage.activatingVersionId === prompt.id"
                  @click="activateVersion(stage, prompt)">
                  <Icon name="check-circle" variant="solid" size="14" class="mr-1"/>
                  Activate
                </v-btn>
              </div>
              <p
                class="prompt-version-content"
                :class="{ expanded: isContentExpanded(prompt.id) }"
                @click="toggleContent(prompt.id)">
                {{ prompt.content }}
              </p>
            </div>
          </div>

          <!-- Add new version -->
          <div v-if="hasWritePermission" class="add-version">
            <v-btn
              v-if="!stage.showAddVersionForm"
              text
              elevation="0"
              class="mank-btn"
              @click="toggleAddVersionForm(stage)">
              <Icon name="plus" variant="solid" size="16" class="mr-1"/>
              Add new version
            </v-btn>
            <div v-else class="add-version-form">
              <MankTextarea
                v-model="stage.newVersionContent"
                placeholder="New prompt content..."
                rows="3"
                hide-details="auto"/>
              <div class="form-actions">
                <v-btn text elevation="0" class="mank-btn" @click="toggleAddVersionForm(stage)">
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  elevation="0"
                  class="mank-btn"
                  :loading="stage.addVersionLoading"
                  :disabled="!stage.newVersionContent.trim()"
                  @click="submitNewVersion(stage)">
                  Save version
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Diff -->
          <div v-if="stage.prompts.length >= 2" class="diff-area">
            <p class="diff-area-title">
              Compare versions
            </p>
            <div class="diff-selects">
              <MankSelect
                v-model="stage.diffFrom"
                :items="versionOptions(stage)"
                item-text="title"
                item-value="value"
                placeholder="From"
                density="compact"
                class="diff-select"/>
              <MankSelect
                v-model="stage.diffTo"
                :items="versionOptions(stage)"
                item-text="title"
                item-value="value"
                placeholder="To"
                density="compact"
                class="diff-select"/>
              <v-btn
                text
                elevation="0"
                class="mank-btn"
                :loading="stage.diffLoading"
                :disabled="stage.diffFrom === null || stage.diffTo === null || stage.diffFrom === stage.diffTo"
                @click="compareDiff(stage)">
                Compare
              </v-btn>
            </div>
            <p v-if="stage.diffError" class="diff-error">
              {{ stage.diffError }}
            </p>
            <PromptDiffViewer v-if="stage.diffResult" :diff="stage.diffResult.diff"/>
          </div>
        </div>
      </div>
    </div>

    <DeleteConfirmationModal
      :visible="showDeleteStageDialog"
      :confirm-loading="deleteStageLoading"
      :detail="deleteStageDetailText"
      @cancel="cancelDeleteStage"
      @confirm="doDeleteStage"/>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '@/components/Icon.vue';
import MankTextField from '@/components/MankTextField.vue';
import MankTextarea from '@/components/MankTextarea.vue';
import MankSelect from '@/components/MankSelect.vue';
import Checkbox from '@/components/Checkbox.vue';
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue';
import PromptDiffViewer from '@/components/PromptDiffViewer.vue';
import AgentStageService from '@/services/AgentStageService';

export default {
  name: 'AgentStagePrompts',
  components: {
    Icon,
    MankTextField,
    MankTextarea,
    MankSelect,
    Checkbox,
    DeleteConfirmationModal,
    PromptDiffViewer,
  },
  props: {
    agentId: {
      type: String,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      stages: [],
      loadingStages: false,
      showAddStageForm: false,
      addStageLoading: false,
      newStage: {
        name: '',
        initial_prompt_content: '',
        is_initial: false,
      },
      showDeleteStageDialog: false,
      deleteStageLoading: false,
      pendingDeleteStage: null,
      expandedContentIds: [],
    };
  },
  computed: {
    ...mapGetters('auth', ['hasPermission']),
    hasReadPermission() {
      return this.hasPermission('read:agent-stage');
    },
    hasWritePermission() {
      return !this.readonly && this.hasPermission('write:agent-stage');
    },
    deleteStageDetailText() {
      if (!this.pendingDeleteStage) return '';
      return `"${this.pendingDeleteStage.name}" stage'i ve tüm prompt versiyonları silinecek.`;
    },
  },
  watch: {
    agentId() {
      this.loadStages();
    },
  },
  mounted() {
    this.loadStages();
  },
  methods: {
    mapStage(apiStage) {
      return {
        ...apiStage,
        prompts: apiStage.prompts || [],
        expanded: false,
        editingMeta: false,
        editForm: {
          name: apiStage.name,
          position: apiStage.position,
          is_initial: apiStage.is_initial,
        },
        savingMeta: false,
        showAddVersionForm: false,
        newVersionContent: '',
        addVersionLoading: false,
        activatingVersionId: null,
        usage: null,
        usageLoading: false,
        usageLoaded: false,
        diffFrom: null,
        diffTo: null,
        diffLoading: false,
        diffResult: null,
        diffError: '',
      };
    },

    notifyError(message) {
      this.$store.dispatch('ui/showNotification', {
        type: 'error',
        title: 'Error',
        message,
      });
    },

    notifySuccess(message) {
      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        title: 'Success',
        message,
      });
    },

    /**
     * Swagger: GET /agent/{agentId}/stage -> GetAgentStageResponse[]
     */
    async loadStages() {
      if (!this.agentId) return;
      this.loadingStages = true;
      try {
        const response = await AgentStageService.listStages(this.agentId);
        if (response?.success) {
          this.stages = (response.data || []).map((stage) => this.mapStage(stage));
        } else {
          throw new Error(response?.message || 'Failed to load stages');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to load stages');
      } finally {
        this.loadingStages = false;
      }
    },

    toggleStage(stage) {
      stage.expanded = !stage.expanded;
      if (stage.expanded && !stage.usageLoaded) {
        this.loadUsage(stage);
      }
    },

    toggleAddStageForm() {
      this.showAddStageForm = !this.showAddStageForm;
      if (!this.showAddStageForm) {
        this.newStage = { name: '', initial_prompt_content: '', is_initial: false };
      }
    },

    /**
     * Swagger: POST /agent/{agentId}/stage
     */
    async submitNewStage() {
      const name = this.newStage.name.trim();
      const initialPromptContent = this.newStage.initial_prompt_content.trim();
      if (!name || !initialPromptContent) return;

      this.addStageLoading = true;
      try {
        const response = await AgentStageService.createStage(this.agentId, {
          name,
          position: this.stages.length,
          is_initial: this.newStage.is_initial,
          initial_prompt_content: initialPromptContent,
        });
        if (response?.success) {
          this.notifySuccess(`Stage "${name}" created`);
          this.showAddStageForm = false;
          this.newStage = { name: '', initial_prompt_content: '', is_initial: false };
          await this.loadStages();
        } else {
          throw new Error(response?.message || 'Failed to create stage');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to create stage');
      } finally {
        this.addStageLoading = false;
      }
    },

    startEditStage(stage) {
      stage.editForm = {
        name: stage.name,
        position: stage.position,
        is_initial: stage.is_initial,
      };
      stage.editingMeta = true;
    },

    cancelEditStage(stage) {
      stage.editingMeta = false;
    },

    /**
     * Swagger: PUT /agent/{agentId}/stage/{stageId}
     */
    async submitEditStage(stage) {
      const name = stage.editForm.name.trim();
      if (!name) return;

      stage.savingMeta = true;
      try {
        const position = Number(stage.editForm.position) || 0;
        const response = await AgentStageService.updateStage(this.agentId, stage.id, {
          name,
          position,
          is_initial: stage.editForm.is_initial,
        });
        if (response?.success) {
          stage.name = name;
          stage.position = position;
          stage.is_initial = stage.editForm.is_initial;
          stage.editingMeta = false;
          this.notifySuccess('Stage updated');
        } else {
          throw new Error(response?.message || 'Failed to update stage');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to update stage');
      } finally {
        stage.savingMeta = false;
      }
    },

    askDeleteStage(stage) {
      if (this.stages.length <= 1) return;
      this.pendingDeleteStage = stage;
      this.showDeleteStageDialog = true;
    },

    cancelDeleteStage() {
      this.showDeleteStageDialog = false;
      this.pendingDeleteStage = null;
    },

    /**
     * Swagger: DELETE /agent/{agentId}/stage/{stageId}
     */
    async doDeleteStage() {
      if (!this.pendingDeleteStage) return;
      const stageName = this.pendingDeleteStage.name;
      this.deleteStageLoading = true;
      try {
        const response = await AgentStageService.deleteStage(
          this.agentId,
          this.pendingDeleteStage.id,
        );
        if (response?.success) {
          this.notifySuccess(`Stage "${stageName}" deleted`);
          this.showDeleteStageDialog = false;
          this.pendingDeleteStage = null;
          await this.loadStages();
        } else {
          throw new Error(response?.message || 'Failed to delete stage');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to delete stage');
        this.showDeleteStageDialog = false;
        this.pendingDeleteStage = null;
      } finally {
        this.deleteStageLoading = false;
      }
    },

    toggleAddVersionForm(stage) {
      stage.showAddVersionForm = !stage.showAddVersionForm;
      if (!stage.showAddVersionForm) {
        stage.newVersionContent = '';
      }
    },

    /**
     * Swagger: POST /agent/{agentId}/stage/{stageId}/prompt
     */
    async submitNewVersion(stage) {
      const content = stage.newVersionContent.trim();
      if (!content) return;

      stage.addVersionLoading = true;
      try {
        const response = await AgentStageService.createPromptVersion(
          this.agentId,
          stage.id,
          { content },
        );
        if (response?.success) {
          this.notifySuccess('New prompt version added');
          stage.newVersionContent = '';
          stage.showAddVersionForm = false;
          await this.refreshStagePrompts(stage);
        } else {
          throw new Error(response?.message || 'Failed to add prompt version');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to add prompt version');
      } finally {
        stage.addVersionLoading = false;
      }
    },

    /**
     * Swagger: GET /agent/{agentId}/stage/{stageId}/prompt
     */
    async refreshStagePrompts(stage) {
      try {
        const response = await AgentStageService.listPromptVersions(this.agentId, stage.id);
        if (response?.success) {
          stage.prompts = response.data || [];
          stage.usageLoaded = false;
          stage.usage = null;
          if (stage.expanded) {
            await this.loadUsage(stage);
          }
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to refresh prompt versions');
      }
    },

    /**
     * Swagger: PATCH /agent/{agentId}/stage/{stageId}/prompt/{promptId}/activate
     */
    async activateVersion(stage, prompt) {
      if (prompt.is_active || !this.hasWritePermission) return;

      stage.activatingVersionId = prompt.id;
      try {
        const response = await AgentStageService.activatePromptVersion(
          this.agentId,
          stage.id,
          prompt.id,
        );
        if (response?.success) {
          await this.refreshStagePrompts(stage);
          this.notifySuccess(`v${prompt.version} is now live`);
        } else {
          throw new Error(response?.message || 'Failed to activate version');
        }
      } catch (error) {
        this.notifyError(error.message || 'Failed to activate version');
      } finally {
        stage.activatingVersionId = null;
      }
    },

    /**
     * Swagger: GET /agent/{agentId}/stage/{stageId}/prompt/usage
     */
    async loadUsage(stage) {
      stage.usageLoading = true;
      try {
        const response = await AgentStageService.getPromptUsage(this.agentId, stage.id);
        if (response?.success) {
          const usageMap = {};
          (response.data || []).forEach((row) => {
            usageMap[row.prompt_version_id] = row.call_count;
          });
          stage.usage = usageMap;
          stage.usageLoaded = true;
        }
      } catch (error) {
        // Kullanım verisi opsiyonel bir gösterge — sessizce yoksayılır
      } finally {
        stage.usageLoading = false;
      }
    },

    usageFor(stage, prompt) {
      if (!stage.usage || stage.usage[prompt.id] === undefined) return null;
      return stage.usage[prompt.id];
    },

    toggleContent(promptId) {
      const index = this.expandedContentIds.indexOf(promptId);
      if (index === -1) {
        this.expandedContentIds.push(promptId);
      } else {
        this.expandedContentIds.splice(index, 1);
      }
    },

    isContentExpanded(promptId) {
      return this.expandedContentIds.includes(promptId);
    },

    versionOptions(stage) {
      return stage.prompts.map((prompt) => ({
        title: `v${prompt.version}${prompt.is_active ? ' (Yayında)' : ''}`,
        value: prompt.version,
      }));
    },

    /**
     * Swagger: GET /agent/{agentId}/stage/{stageId}/prompt/diff?from=&to=
     */
    async compareDiff(stage) {
      if (stage.diffFrom === null || stage.diffTo === null || stage.diffFrom === stage.diffTo) {
        return;
      }

      stage.diffLoading = true;
      stage.diffError = '';
      try {
        const response = await AgentStageService.getPromptDiff(
          this.agentId,
          stage.id,
          stage.diffFrom,
          stage.diffTo,
        );
        if (response?.success) {
          stage.diffResult = response.data;
        } else {
          stage.diffResult = null;
          stage.diffError = response?.message || 'Failed to compare versions';
        }
      } catch (error) {
        stage.diffResult = null;
        stage.diffError = error.message || 'Failed to compare versions';
      } finally {
        stage.diffLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.agent-stage-prompts {
  margin-top: 24px;

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;

    .section-title {
      margin: 0;
    }
  }

  .stages-loading,
  .stages-empty {
    padding: 24px;
    text-align: center;
    color: $light-foreground-3;
    font-size: $body-3-size;
    border: 1px dashed $light-border;
    border-radius: 8px;
  }

  .stage-card {
    border: 1px solid $light-border;
    border-radius: 8px;
    padding: 16px;
    background-color: $light-surface-2;
    margin-bottom: 16px;
  }

  .form-group {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
  }

  .stages-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .stage-accordion {
    border: 1px solid $light-border;
    border-radius: 8px;
    overflow: hidden;

    .stage-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 10px 12px;
      background-color: $light-surface-1;
      cursor: pointer;

      .stage-header-left {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;

        .stage-title {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-weight: 700;
          font-size: $body-3-size;
          color: $light-foreground-1;
          margin: 0;
        }
      }

      .stage-header-actions {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
      }

      .expand-btn {
        transition: transform 0.2s ease;

        &.expanded {
          transform: rotate(180deg);
        }
      }
    }

    .stage-content {
      padding: 16px;
      background-color: $light-surface-2;
      display: flex;
      flex-direction: column;
      gap: 16px;
    }
  }

  .stage-edit-form {
    border: 1px solid $light-border;
    border-radius: 8px;
    padding: 12px;
    background-color: $light-surface-1;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 100px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    font-size: $caption-1-size;
    line-height: 1.4;
    white-space: nowrap;

    &-muted {
      color: $light-foreground-3;
      background-color: $light-surface-3;
    }

    &-primary {
      color: $light-primary;
      background-color: $light-primary-light;
    }

    &-success {
      color: $light-success;
      background-color: $light-success-light;
    }
  }

  .prompt-versions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .prompt-version-row {
    border: 1px solid $light-border;
    border-radius: 8px;
    padding: 10px 12px;

    &.is-active {
      border-color: $light-success-neutral;
    }

    .prompt-version-header {
      display: flex;
      align-items: center;
      gap: 8px;

      .usage-text {
        font-size: $caption-1-size;
        color: $light-foreground-4;
      }

      .prompt-version-spacer {
        flex: 1;
      }

      .activate-btn {
        min-width: 0;
      }
    }

    .prompt-version-content {
      margin: 8px 0 0;
      font-size: $body-3-size;
      color: $light-foreground-3;
      cursor: pointer;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      white-space: pre-wrap;

      &.expanded {
        -webkit-line-clamp: unset;
      }
    }
  }

  .add-version-form {
    display: flex;
    flex-direction: column;
  }

  .diff-area {
    border-top: 1px solid $light-border;
    padding-top: 16px;

    .diff-area-title {
      font-weight: 700;
      font-size: $body-3-size;
      color: $light-foreground-1;
      margin: 0 0 8px;
    }

    .diff-selects {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;

      .diff-select {
        max-width: 160px;
      }
    }

    .diff-error {
      color: $light-error;
      font-size: $caption-1-size;
      margin-bottom: 8px;
    }
  }
}
</style>
